import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { DubLevelSlider, formatDubLevel } from './DubLevelSlider';

interface DubLevelRowProps {
  /** The level the clip is saved at, 0–100. */
  level: number;
  /**
   * Save a level. Called once when the slider is let go, never per step, so
   * one drag is one save and one undo entry.
   */
  onChange: (level: number) => void;
  /** PLAT-11927 — the row's name; the Original Level row reuses this one. */
  label?: string;
}

/**
 * PLAT-11899 — the language menu's Dub Level row, under the Voice toggle. On
 * an editing surface every control saves as it is used (the Voice toggle
 * beside it does), so this does too: the slider follows the pointer, and the
 * level is saved when it is let go.
 */
export const DubLevelRow: React.FC<DubLevelRowProps> = ({
  level,
  onChange,
  label = 'Dub Level',
}) => {
  // Sentence case for assistive tech, as the other menu controls are named.
  const name = label.charAt(0) + label.slice(1).toLowerCase();
  // What the slider shows mid-drag, before the release saves it.
  const [draft, setDraft] = useState(level);
  // An Undo, or a save from elsewhere (Dubbing Review's Apply to Clip), moves
  // the saved level underneath the row.
  useEffect(() => setDraft(level), [level]);

  return (
    <Row role="group" aria-label={name}>
      <span className="label">{label}</span>
      <div className="control">
        <DubLevelSlider
          value={draft}
          onChange={setDraft}
          onCommit={next => {
            if (next !== level) onChange(next);
          }}
          ariaLabel={name}
          width={140}
        />
        <span className="value" data-testid="dub-level-readout">
          {formatDubLevel(draft)}
        </span>
      </div>
    </Row>
  );
};

// The Voice row's own measurements (TranslationToggle's `VoiceSlotRow`), so
// the header reads as one stack of label-left, control-right lines.
const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px 8px;

  .label {
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semibold);
    color: var(--text-muted);
  }
  .control {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .value {
    width: 40px;
    text-align: right;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: var(--text-secondary);
    font-variant-numeric: tabular-nums;
  }
`;
